import { v4 as uuidv4 } from 'uuid';
import { computed, inject } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { keyBy, merge, sortBy, uniq, values } from 'lodash';

import { Routes } from '~/constants/routes';
import { AddToastNotification } from '~/constants/mutations';
import {
  AddDownloadInProgress,
  DeleteAuthoredDocumentById,
  RemoveDownloadInProgress,
  SetAuthoredDocuments,
  SetSelectedAuthoredDocument,
  SetSelectedReferencePanel,
  SetTotalMediaImages,
  UpdateMessageReferences,
  UpdateSystemMessageTranslationData,
} from '~/constants/actions';
import {
  GetAuthoredDocuments,
  GetSelectedAuthoredDocument,
  GetSelectedReference,
  GetTotalMediaImages,
  IsDownloadInProgress,
} from '~/constants/getters';
import { MEDIA_TYPE } from '~/constants/documents';

import { thumbnailCache } from '~/services/thumbnail-cache-service';
import { fetchDocumentThumbnails } from '~/utils/thumbnail-cache-utils';
import useModals from '~/composables/useModals';
import useTranslation from '~/composables/useTranslation';
import { apiGatewayEndpoints } from '~/services/arcane-api-gateway/api-gateway-endpoints';
import { fetchMediaImages as fetchMediaImagesApi } from '~/services/api/images';
import { mapMetadataFields } from '~/utils/document-utils';

export default () => {
  const store = useStore();
  const router = useRouter();
  const arcaneApiGateway = inject('arcaneApiGateway');
  const { showPreviewDocument } = useModals();
  const { setCurrentPdfLanguage } = useTranslation();

  const authoredDocuments = computed(() => store.getters[`documentStore/${GetAuthoredDocuments}`]);
  const selectedAuthoredDocument = computed(() => store.getters[`documentStore/${GetSelectedAuthoredDocument}`]);
  const selectedReference = computed(() => store.getters[`documentStore/${GetSelectedReference}`]);
  const totalMediaImages = computed(() => store.getters[`documentStore/${GetTotalMediaImages}`]);

  const setAuthoredDocuments = (data) => store.dispatch(`documentStore/${SetAuthoredDocuments}`, data);

  const setTotalMediaImages = (data) => store.dispatch(`documentStore/${SetTotalMediaImages}`, data);

  const setSelectedAuthoredDocument = (data) => store.dispatch(`documentStore/${SetSelectedAuthoredDocument}`, data);

  const setMessageReferencesThumbs = async (messageId, data) => {
    const { references = [], citedReferences = [] } = data;

    for (const reference of references) {
      const { pageNumber } = reference;

      // transform the STRING items to INT, so we can sort and remove duplicates
      let pages = [1, ...(pageNumber ? pageNumber : [])].map((item) => Number(item));
      pages = sortBy(uniq(pages));

      reference.pageNumber = pages;
    }

    const documents = references.map(({ externalId, pageNumber }) => {
      return { id: externalId, pages: pageNumber };
    });
    const response = await getDocumentThumbs({ documents });

    const updatedReferences = values(merge(keyBy(response, 'documentId'), keyBy(references, 'externalId')));
    const updatedCitedReferences = citedReferences.map((citedRef) => {
      const responseData = response.find((resp) => resp.documentId === citedRef?.externalId);
      return responseData ? { ...citedRef, ...responseData } : citedRef;
    });

    store.dispatch(`conversationStore/${UpdateMessageReferences}`, {
      messageId,
      data: { ...data, references: updatedReferences, citedReferences: updatedCitedReferences },
    });
  };

  const updateSystemMessageTranslationData = async (messageId, data) => {
    const response = await getDocumentThumbs({ documents: [{ id: data.externalId, pages: [1] }] });
    const newData = { ...data, pages: response.length ? response[0].pages : {} };
    store.dispatch(`conversationStore/${UpdateSystemMessageTranslationData}`, { messageId, data: [newData] });
  };

  const getDocumentThumbs = async ({ documents = [], size = 'small', service }) => {
    try {
      const fetchFromApi = async (uncachedDocuments) => {
        if (service) {
          const { data } = await service.post(apiGatewayEndpoints.s3Docs.pageCovers, {
            documents: uncachedDocuments,
          });
          return data;
        }
        const { data } = await arcaneApiGateway.post(apiGatewayEndpoints.s3Docs.pageCovers, {
          documents: uncachedDocuments,
        });
        return data;
      };

      return await fetchDocumentThumbnails(documents, fetchFromApi, size);
    } catch (error) {
      console.error('Failed to load documents thumbs:', error);
      return [];
    }
  };

  const setSelectedDocument = async ({ reference, isMetadata = true, language = null, id = null }) => {
    let data;
    let currentReference = reference;

    if (currentReference) {
      if (isMetadata && currentReference.documentType !== 'tempDocument') {
        ({ data } = await arcaneApiGateway.post(apiGatewayEndpoints.documents.metadata, {
          externalIds: [currentReference.externalId],
        }));
      }
    } else if (id) {
      ({ data } = await arcaneApiGateway.post(apiGatewayEndpoints.documents.metadata, {
        externalIds: [id],
      }));
      // Use metadata response as the reference when no reference object was passed
      if (data && data[0]) {
        currentReference = { ...data[0], externalId: data[0].externalId || id };
      }
    }

    if (!currentReference) {
      console.warn('setSelectedDocument: no reference or id provided');
      return;
    }

    const metadataFields = data ? data[0] : {};

    if (!currentReference.pages) {
      currentReference.pages = {};
      currentReference.pages[1] = {};
    }

    const largeThumb = await getDocumentThumbs({
      documents: [{ id: currentReference.externalId, pages: [1] }],
      size: 'large',
    });
    currentReference.pages[1].large = largeThumb[0]?.pages[1]?.large ?? '';
    if (language) {
      setCurrentPdfLanguage(language);
    } else {
      setCurrentPdfLanguage(metadataFields?.languageCode);
    }

    store.dispatch(`documentStore/${SetSelectedReferencePanel}`, mapMetadataFields(currentReference, metadataFields));
  };

  const getSourceTranslationAvailable = async (externalId) => {
    const {
      data: { result },
    } = await arcaneApiGateway.post(apiGatewayEndpoints.translation.sourceTranslationAvailable, {
      externalId,
    });
    return result;
  };

  const getPdfUrl = async (externalId, languageCode) => {
    try {
      const { data } = await arcaneApiGateway.post(apiGatewayEndpoints.s3Docs.pdfUrl, {
        documentId: externalId,
        language: languageCode,
      });
      return data.url;
    } catch (error) {
      console.error('Failed to load PDF source:', error);
    }
  };

  const getSourceUrl = async (externalId, languageCode) => {
    try {
      const { data } = await arcaneApiGateway.post(apiGatewayEndpoints.s3Docs.sourceUrl, {
        documentId: externalId,
        language: languageCode,
      });
      return data.url;
    } catch (error) {
      console.error('Failed to load zip source:', error);
    }
  };

  const retrieveAuthoredDocumentThumbs = async (documents = []) => {
    try {
      const { data } = await arcaneApiGateway.post(apiGatewayEndpoints.s3Docs.thumbnail, {
        documents,
      });
      return data;
    } catch (error) {
      console.error('Failed to load documents thumbs:', error);
      return [];
    }
  };

  const getAuthoredDocumentThumbs = async (
    externalIds = [],
    forceRefresh = false,
    includeCroppedVariants = false,
    authDocumentId = '',
  ) => {
    try {
      // Check cache for all variants (original + cropped)
      const uncachedIds = [];
      const cachedResults = [];

      for (const externalId of externalIds) {
        if (forceRefresh) {
          // Skip cache when force refresh is requested
          uncachedIds.push(externalId);
          continue;
        }

        const cachedUrl = thumbnailCache.get(externalId, MEDIA_TYPE.imported, 'original');
        if (cachedUrl) {
          cachedResults.push({
            id: externalId,
            url: cachedUrl,
            name: 'original.png',
            variant: 'original',
          });
          // Check if we have any cached data for this externalId
          const cachedImages = thumbnailCache.getAllVariants(externalId, MEDIA_TYPE.imported);

          if (cachedImages && cachedImages.length > 0) {
            cachedResults.push(...cachedImages);
          }
        } else {
          uncachedIds.push(externalId);
        }
      }

      if (uncachedIds.length === 0) {
        return cachedResults;
      }

      const { data } = await arcaneApiGateway.post(apiGatewayEndpoints.s3Docs.thumbnailImportedImages, {
        externalIds: uncachedIds,
        includeCroppedVariants,
        authDocumentId,
      });

      // Cache all variants (original, cropped, etc.)
      for (const image of data) {
        if (image.url && image.variant) {
          thumbnailCache.set(image.id, MEDIA_TYPE.imported, image.url, image.variant);
        }
      }

      return [...cachedResults, ...data];
    } catch (error) {
      console.error('Failed to load documents thumbs:', error);
      return [];
    }
  };

  const getAuthoredDocuments = async () => {
    try {
      const { data } = await arcaneApiGateway.get(apiGatewayEndpoints.authoredDocuments.getAuthoredDocuments);
      const documents = (data || []).map(({ id, externalId }) => ({ id, externalId }));
      const thumbs = await retrieveAuthoredDocumentThumbs(documents);
      const updatedDocuments = values(merge(keyBy(data, 'id'), keyBy(thumbs, 'id')));
      setAuthoredDocuments(updatedDocuments);

      return data.data;
    } catch (error) {
      console.log('Failed to save retrieve Document', error);
    }
  };

  const getAuthoredDocumentById = async (id) => {
    try {
      const { data } = await arcaneApiGateway.get(apiGatewayEndpoints.authoredDocuments.getAuthoredDocumentById(id));

      return data[0];
    } catch (error) {
      console.log('Failed to load Authored Document', error);
      return [];
    }
  };

  const deleteAuthoredDocumentById = async (id) => {
    try {
      await arcaneApiGateway.delete(apiGatewayEndpoints.authoredDocuments.deleteAuthoredDocumentById(id));

      // Remove the document from the local store state
      store.dispatch(`documentStore/${DeleteAuthoredDocumentById}`, id);
      return id;
    } catch (error) {
      console.log('Failed to delete Authored Document', error);
      return null;
    }
  };

  const downloadAuthoredDocument = async (id) => {
    // Check if download is already in progress for this document
    if (store.getters[`documentStore/${IsDownloadInProgress}`](id)) {
      console.log('Download already in progress for this document');
      return '';
    }

    try {
      // Add document to downloads in progress
      const response = await arcaneApiGateway.get(apiGatewayEndpoints.authoredDocuments.downloadAuthoredDocument(id));

      // show toast that archive is getting ready
      if (response.status === 202) {
        store.dispatch(`documentStore/${AddDownloadInProgress}`, id);

        store.commit(`notificationStore/${AddToastNotification}`, {
          type: 'EMAIL_ARCHIVE_PENDING',
          title: "Download request submitted. You will receive a notification when it's ready.",
          notification: { externalId: uuidv4() },
          createdAt: Date.now(),
        });

        return '';
      }

      store.dispatch(`documentStore/${RemoveDownloadInProgress}`, id);
      return response.data.url;
    } catch (error) {
      // Clear progress state on error
      store.dispatch(`documentStore/${RemoveDownloadInProgress}`, id);

      store.commit(`notificationStore/${AddToastNotification}`, {
        type: 'EMAIL_ARCHIVE_ERROR',
        title: 'Failed to request download for Authored Document',
        notification: { externalId: uuidv4() },
        createdAt: Date.now(),
      });

      return '';
    }
  };

  const fetchMediaImages = async ({ query, orgId, filter, from, size }) => {
    try {
      const response = await fetchMediaImagesApi({ query, orgId, filter, from, size });

      return response.data;
    } catch (error) {
      console.log('Failed to fetch media images', error);
      return [];
    }
  };

  const openDocumentFromTitle = async (payload, id) => {
    await setSelectedDocument({ reference: payload.reference, id });
    showPreviewDocument(payload.page);
  };

  const duplicateAuthoredDocument = async (parentId) => {
    try {
      const duplicateAuthoredDocument = await arcaneApiGateway.post(
        apiGatewayEndpoints.authoredDocuments.duplicateAuthoredDocument,
        { parentId },
      );
      const conversationId = duplicateAuthoredDocument?.data?.id || '';
      await router.push({ name: Routes.Authoring.name, params: { conversationId: `${conversationId}` } });
    } catch (error) {
      console.log('Failed to duplicate Authored Document', error);
      return null;
    }
  };

  const getAuthoredUserImages = async (authDocumentId, ids = [], includeCroppedVariants = false) => {
    try {
      // Simple caching for user images
      const uncachedIds = [];
      const cachedResults = [];

      for (const id of ids) {
        const cachedUrl = thumbnailCache.get(id, MEDIA_TYPE.documentUserImages, 'original');
        if (cachedUrl) {
          const cachedImages = thumbnailCache.getAllVariants(id, MEDIA_TYPE.documentUserImages);
          if (cachedImages && cachedImages.length > 0) {
            cachedResults.push(...cachedImages);
          } else {
            cachedResults.push({
              id,
              url: cachedUrl,
              name: 'original.png',
              variant: 'original',
            });
          }
        } else {
          uncachedIds.push(id);
        }
      }

      if (uncachedIds.length === 0) {
        return cachedResults;
      }

      const { data } = await arcaneApiGateway.post(apiGatewayEndpoints.s3Docs.authoredUserImages, {
        ids: uncachedIds,
        authDocumentId,
        includeCroppedVariants,
      });

      // Cache all variants returned from BE
      // Backend returns variant field: 'original' or 'cropped'
      for (const image of data) {
        if (image.url && image.variant) {
          // For cropped images, use the name as cache key to support multiple crops
          const cacheVariant = image.variant === 'cropped' && image.name ? image.name : image.variant;
          thumbnailCache.set(image.id, MEDIA_TYPE.documentUserImages, image.url, cacheVariant);
        }
      }

      return [...cachedResults, ...data];
    } catch (error) {
      console.error('Failed to load user image thumbs:', error);
      return [];
    }
  };

  return {
    duplicateAuthoredDocument,
    deleteAuthoredDocumentById,
    getDocumentThumbs,
    getPdfUrl,
    getSourceUrl,
    setSelectedDocument,
    setMessageReferencesThumbs,
    getSourceTranslationAvailable,
    updateSystemMessageTranslationData,
    getAuthoredDocuments,
    setSelectedAuthoredDocument,
    selectedAuthoredDocument,
    authoredDocuments,
    getAuthoredDocumentById,
    selectedReference,
    downloadAuthoredDocument,
    getAuthoredDocumentThumbs,
    fetchMediaImages,
    totalMediaImages,
    setTotalMediaImages,
    openDocumentFromTitle,
    getAuthoredUserImages,
  };
};
